// An operation's Notes panel (#office-notes): the list, add a note, delete one (with a confirm).
// Split out of app.js (docs/split-plan.md, session 3). The app.js helpers it calls come in through
// setNotesPanelDeps() (see estimates.js for why it doesn't import app.js back).
import { DB_URL } from './firebaseConfig';
import { techLabel } from './techNames';
import { escapeHTML, fetchIdToken, generateId, getDB, toastMessage, updateTimestamps } from './utilities';

let shell = null;
export function setNotesPanelDeps(deps) { shell = deps; }

// ---------- Add Note panel — single source of truth ----------
// Replaces 4 independently-drifted copies of this same note-row/delete-
// confirm markup (createOperationRow's notes button, buildOpTableRow's, the
// techs-list notes button, and two byte-for-byte-identical note-submit
// handlers below) — the same kind of drift that left the notes icon's
// "has notes" state inconsistent (see the notes-icon fix elsewhere this
// session). Also fixes a real gap: only buildOpTableRow's version ever
// tagged its trigger button `.note-active`, so adding/clearing notes from
// the other two entry points silently no-opped (or threw, in
// checkNoteCount()) on a null `.note-active img`. openNotesPanel() now
// tags the trigger consistently for all callers.

function buildNoteRow(note, key, jobId) {
  const noteContainer = document.createElement('div');
  noteContainer.className = 'note-pair';
  noteContainer.id = key;

  const noteText = document.createElement('p');
  noteText.className = 'note-p';
  noteText.textContent = note.Note;

  const noteMeta = document.createElement('div');
  noteMeta.className = 'note-info-tech';
  noteMeta.innerHTML = `<span class="note-tech-name">${escapeHTML(techLabel(note.Tech))}</span><span class="note-timestamp">${escapeHTML(note.Date || '')}, ${escapeHTML(note.Time || '')}</span>`;

  const deleteBtn = document.createElement('button');
  deleteBtn.type = 'button';
  deleteBtn.className = 'note-delete-btn';
  deleteBtn.setAttribute('aria-label', 'Delete note');
  deleteBtn.innerHTML = '&times;';
  deleteBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    openNoteDeleteConfirm(jobId, key, noteContainer);
  });

  noteContainer.appendChild(noteText);
  noteContainer.appendChild(noteMeta);
  noteContainer.appendChild(deleteBtn);
  return noteContainer;
}

function openNoteDeleteConfirm(jobId, noteId, noteContainer) {
  const confirmationModal = document.createElement('div');
  confirmationModal.className = 'confirmation-note-delete-modal';
  confirmationModal.innerHTML = `
    <div class="note-delete-modal-content">
      <p>Delete this note?</p>
      <div class="note-delete-modal-actions">
        <button type="button" class="cancel-delete">Cancel</button>
        <button type="button" class="confirm-delete">Delete</button>
      </div>
    </div>
  `;
  confirmationModal.querySelector('.confirm-delete').addEventListener('click', async () => {
    await deleteNotes(jobId, noteId);
    noteContainer.remove();
    confirmationModal.remove();
    shell.checkNoteCount();
  });
  confirmationModal.querySelector('.cancel-delete').addEventListener('click', () => confirmationModal.remove());
  confirmationModal.addEventListener('click', (e) => { if (e.target === confirmationModal) confirmationModal.remove(); });
  document.body.appendChild(confirmationModal);
}

function renderNotesList(notes, jobId) {
  const noteArea = document.getElementById('note-area');
  noteArea.innerHTML = '';
  const entries = notes ? Object.entries(notes) : [];
  if (!entries.length) {
    noteArea.innerHTML = '<div class="note-empty-state">No notes yet</div>';
    return;
  }
  entries.forEach(([key, note]) => noteArea.appendChild(buildNoteRow(note, key, jobId)));
}

// Every "click a notes icon" entry point funnels through here now — tags
// `triggerBtn` .note-active (clearing any previous one) so submitNote()/
// checkNoteCount() can reliably find the right icon to update.
export async function openNotesPanel(jobId, triggerBtn) {
  const jobNotesData = await shell.getJobData(jobId);

  document.querySelectorAll('.note-active').forEach((el) => el.classList.remove('note-active'));
  if (triggerBtn) triggerBtn.classList.add('note-active');

  document.getElementById('office-notes').classList.add('active');
  document.getElementById('note-job-id').value = jobId;

  // Panel header previously just said "Notes" with no indication of which operation it's for —
  // easy to lose track of when opening notes on several ops in a row. Data's already fetched above.
  const subtitleEl = document.getElementById('note-panel-subtitle');
  if (subtitleEl) {
    const shortDesc = (jobNotesData?.Short_Description_DM || jobNotesData?.Description || '').split(':')[0];
    subtitleEl.textContent = jobNotesData ? [jobNotesData.OpCode, shortDesc].filter(Boolean).join(' · ') : '';
  }

  renderNotesList(jobNotesData?.Notes, jobId);

  const noteTextEl = document.getElementById('note-text');
  autosizeNoteInput(noteTextEl);
  noteTextEl?.focus();
}

function autosizeNoteInput(el) {
  const noteTextEl = el || document.getElementById('note-text');
  if (!noteTextEl) return;
  noteTextEl.style.height = 'auto';
  noteTextEl.style.height = Math.min(noteTextEl.scrollHeight, 120) + 'px';
}

async function submitNote() {
  const jobId = document.getElementById('note-job-id').value;
  const noteTextEl = document.getElementById('note-text');
  const note = noteTextEl.value.trim();
  if (!note || !jobId) return;

  const tech = await shell.getCurrentUserData();
  const newId = await generateId();
  const noteDate = await shell.getFormattedDate();
  const noteTime = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const newNote = { Note: note, Tech: tech.displayName, Date: noteDate, Time: noteTime, Job_ID: jobId, id: newId };

  await addJobNote(jobId, newNote);

  noteTextEl.value = '';
  autosizeNoteInput(noteTextEl);

  document.querySelector('.note-active img')?.classList.add('has-notes');

  const noteArea = document.getElementById('note-area');
  noteArea.querySelector('.note-empty-state')?.remove();
  noteArea.appendChild(buildNoteRow(newNote, newId, jobId));
  noteArea.scrollTop = noteArea.scrollHeight;
}

document.getElementById('note-text').addEventListener('input', () => autosizeNoteInput());

document.getElementById('note-text').addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' || e.shiftKey) return;
  e.preventDefault();
  submitNote();
});

document.getElementById('note-submit').addEventListener('click', submitNote);

// Adds one note to an operation. Only that note is written: rewriting the whole Notes list from
// this device's copy dropped any note someone else had just added.
export async function addJobNote(jobId, note) {
  const idToken = await fetchIdToken();
  const response = await fetch(`${DB_URL}/data/${jobId}.json?auth=${idToken}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [`Notes/${note.id}`]: note }),
  });
  if (!response.ok) {
    toastMessage('Note not saved: check the connection and try again.', 'red', 4000);
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  try {
    const db = await getDB();
    const store = db.transaction('jobData', 'readwrite').objectStore('jobData');
    const rec = await new Promise((resolve, reject) => { const r = store.get(jobId); r.onsuccess = () => resolve(r.result || null); r.onerror = () => reject(r.error); });
    if (rec) { rec.Notes = { ...(rec.Notes && typeof rec.Notes === 'object' ? rec.Notes : {}), [note.id]: note }; store.put(rec); }
  } catch (e) { console.error('[notes] cache update failed', e); }
  updateTimestamps('jobData', 'data');
}

export async function updateJobNotes(jobId, index, value) {
  try {
    // Update Firebase Realtime Database
    const idToken = await fetchIdToken();
    const firebaseUrl = `${DB_URL}/data/${jobId}.json?auth=${idToken}`;

    const partialUpdate = { [index]: value };

    const options = {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(partialUpdate),
    };

    const response = await fetch(firebaseUrl, options);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const result = await response.json();
    console.log("Firebase update successful", result);

    // Update IndexedDB
    await updateJobNotesDB(jobId, index, value);
    updateTimestamps('jobData', 'data');
    console.log("IndexedDB update successful");
  } catch (error) {
    console.error("Error updating job info:", error);
  }
}

async function updateJobNotesDB(jobId, index, value) {
  try {
    const db = await getDB();
    const transaction = db.transaction("jobData", "readwrite");
    const objectStore = transaction.objectStore("jobData");

    const itemToUpdate = await new Promise((resolve, reject) => {
      const req = objectStore.get(jobId);
      req.onerror = () => {
        console.error("Error fetching data");
        reject(req.error);
      };
      req.onsuccess = () => resolve(req.result || null);
    });

    if (!itemToUpdate) {
      console.error(`No item found with Job_ID ${jobId}`);
      return;
    }

    itemToUpdate[index] = value;

    await new Promise((resolve, reject) => {
      const req = objectStore.put(itemToUpdate);

      req.onsuccess = function () {
        console.log(`Data updated for Job_ID ${jobId}`);
        resolve();
      };

      req.onerror = function () {
        console.error("Error updating data");
        reject(req.error);
      };
    });
  } catch (error) {
    console.log("Error updating IndexedDB", error);
  }
}


async function deleteNotes(jobId, noteId) {

  try {
    // Delete from Firebase Realtime Database
    const idToken = await fetchIdToken();
    const firebaseUrl = `${DB_URL}/data/${jobId}/Notes/${noteId}.json?auth=${idToken}`;

    const options = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await fetch(firebaseUrl, options);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    console.log("Firebase delete successful");

    // Delete from IndexedDB
    await deleteNotesDB(jobId, noteId);
    updateTimestamps('jobData', 'data');
    console.log("IndexedDB delete successful");

  } catch (error) {
    console.error("Error deleting note:", error);
  }
}

async function deleteNotesDB(jobId, noteId) {
  console.log(jobId, noteId);

  try {
    const db = await getDB();
    const transaction = db.transaction("jobData", "readwrite");
    const objectStore = transaction.objectStore("jobData");

    const itemToUpdate = await new Promise((resolve, reject) => {
      const req = objectStore.get(jobId);
      req.onerror = () => {
        console.error("Error fetching data");
        reject(req.error);
      };
      req.onsuccess = () => resolve(req.result || null);
    });

    if (!itemToUpdate) {
      console.error(`No item found with Job_ID ${jobId}`);
      return;
    }

    if (!itemToUpdate.Notes || !itemToUpdate.Notes[noteId]) {
      console.error("Note not found:", noteId);
      return;
    }

    delete itemToUpdate.Notes[noteId];

    await new Promise((resolve, reject) => {
      const req = objectStore.put(itemToUpdate);

      req.onsuccess = function () {
        console.log(`Note deleted for Job_ID ${jobId}`);
        resolve();
      };

      req.onerror = function () {
        console.error("Error deleting note");
        reject(req.error);
      };
    });
  } catch (error) {
    console.log("Error updating IndexedDB", error);
  }
}
